import React, { useState } from 'react';
import { toast } from 'sonner';
import { Target, Loader2, ShieldAlert, CheckCircle2, XCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { runAiJob } from '../../lib/aiJob';
import { fmtDate } from '../../lib/dates';

const STYLE = { estricto: 'Estricto', formalista: 'Formalista', garantista: 'Garantista' };
const List = ({ title, items, icon: Icon, color, testid }) => items?.length > 0 && (
  <div data-testid={testid}>
    <p className={`mb-1 text-xs font-semibold ${color}`}>{title}</p>
    <ul className="space-y-1">{items.map((x, i) => <li key={i} className="flex gap-1.5 text-xs text-slate-300"><Icon className={`mt-0.5 h-3 w-3 shrink-0 ${color}`} />{x}</li>)}</ul>
  </div>
);

// How this minister / prosecutor acts (procedural + administrative) and how to win before them
export const JudgeStrategyProfile = ({ judgeId, initial }) => {
  const { api } = useAuth();
  const [p, setP] = useState(initial || null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { setP(await runAiJob(api, `/courts/members/${judgeId}/strategy`, {}, { timeout: 240000 })); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <section data-testid="judge-strategy-profile" className="mt-8 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-emerald-200"><Target className="h-4 w-4" />Perfil estratégico: cómo ganar ante este magistrado</h2>
        {p && <span data-testid="judge-strategy-style" className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] text-amber-300">{STYLE[p.estilo]}</span>}
        <button data-testid="judge-strategy-btn" onClick={run} disabled={busy} className="ml-auto flex items-center gap-1 rounded-full bg-emerald-500 px-4 py-1.5 text-xs font-semibold text-slate-950 disabled:opacity-60">
          {busy ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Analizando (≈1 min)...</> : p ? 'Actualizar perfil' : 'Analizar perfil y estrategia'}
        </button>
      </div>
      {!p ? <p className="mt-3 text-sm text-slate-400">Analiza su forma de actuar en lo procesal y en lo administrativo, qué valora, qué castiga y cómo responderle un oficio o enfrentar un sumario.</p> : (
        <div className="mt-4 space-y-4">
          <p className="text-sm text-slate-200">{p.resumen}</p>
          <div className="grid gap-4 md:grid-cols-2">
            <List testid="judge-strategy-procesal" title="En lo procesal" items={p.perfil_procesal} icon={CheckCircle2} color="text-sky-300" />
            <List testid="judge-strategy-administrativo" title="En lo administrativo y disciplinario" items={p.perfil_administrativo} icon={CheckCircle2} color="text-sky-300" />
            <List testid="judge-strategy-ganar" title="Cómo ganar" items={p.como_ganar} icon={CheckCircle2} color="text-emerald-300" />
            <List testid="judge-strategy-evitar" title="Qué evitar" items={p.que_evitar} icon={XCircle} color="text-red-300" />
          </div>
          {p.tono_escritos && <p data-testid="judge-strategy-tono" className="rounded-xl border border-sky-500/30 bg-sky-500/5 p-3 text-xs text-sky-100"><b>Tono ideal de escritos y alegatos:</b> {p.tono_escritos}</p>}
          {p.en_oficios_y_sumarios && <p data-testid="judge-strategy-oficios" className="flex gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-100"><ShieldAlert className="h-4 w-4 shrink-0 text-amber-300" />{p.en_oficios_y_sumarios}</p>}
          <p data-testid="judge-strategy-source" className="text-[10px] text-slate-500">
            {p.source === 'fallos_reales' ? `Basado en ${p.based_on_rulings} fallo(s) reales de la plataforma + análisis de la IA` : 'Solo análisis de la IA (aún sin fallos suyos en la plataforma)'} · {p.base}{p.learned_from && (p.learned_from.resultados || p.learned_from.simulaciones) ? ` · Fidem aprendió de ${p.learned_from.resultados} causa(s) real(es) (${p.learned_from.ganadas} ganadas) y ${p.learned_from.simulaciones} simulación(es)` : ''} · {fmtDate(p.generated_at)} · estimación, no oficial
          </p>
        </div>
      )}
    </section>
  );
};
